// The person's tasks as Quanty One follows them (tasks.md section 13): GET /api/tasks, and the status line.
import { dividerLabel, type Zone } from "./times.js";

export interface TaskWait {
  handle: string;
  status: string;
}

export interface TaskItem {
  taskId: string;
  description: string;
  label: string;
  review: boolean;
  actionable: boolean;
  waitingOn: TaskWait[];
  question?: string | null;
  result?: string | null;
  reason?: string | null;
}

export interface TaskTree {
  rootTaskId: string;
  request: string;
  state: string;
  createdAt?: string;
  tasks: TaskItem[];
}

export interface TaskCounters {
  turnsUsed: number;
  noProgress: number;
  sinceFinish?: number;
  stoppedBy?: "time" | "stuck";
}

export interface TasksView {
  driver: {
    state: "active" | "paused" | "idle";
    pausedReason?: "stopped" | "credits" | "failures";
    blocked?: "reconnect_required";
    trees: Record<string, TaskCounters>;
  };
  live: boolean;
  trees?: TaskTree[];
  snapshot?: string;
}

const LABELS: Record<string, string> = {
  to_do: "To do",
  doing: "Doing",
  waiting: "Waiting",
  waiting_on_you: "Needs your answer",
  blocked: "Blocked",
  done: "Done",
  failed: "Failed",
  dropped: "Dropped",
};

export const taskLabel = (label: string): string => LABELS[label] ?? label;

/** "2 of 6 tasks done · started Today 4:18 PM" for a tree: progress in tasks, never in turns. */
export function progressLine(tree: TaskTree, now: number, zone: Zone = {}): string {
  const tasks = tree.tasks.filter((t) => !t.review);
  const done = tasks.filter((t) => ["done", "failed", "dropped"].includes(t.label)).length;
  const started = tree.createdAt ? Date.parse(tree.createdAt) : NaN;
  return `${done} of ${tasks.length} tasks done${Number.isNaN(started) ? "" : ` · started ${dividerLabel(started, now, zone)}`}`;
}

/** Why a tree stopped, for the drawer. */
export function stopLine(counters: TaskCounters | undefined): string | undefined {
  switch (counters?.stoppedBy) {
    case "time":
      return "Stopped: the time you gave it is up. Ask Quanty One in the chat for more time.";
    case "stuck":
      return "Stopped: it was no longer making progress. Resume to let it try again.";
    default:
      return undefined;
  }
}

/**
 * The status line where "Quanty One is working" shows while a turn runs, for when none does: what the
 * driver is doing with the person's tasks. Undefined with no open tasks.
 */
export function statusLine(view: TasksView | undefined): string | undefined {
  if (!view || view.driver.state === "idle") return undefined;
  const open = (view.trees ?? []).filter((t) => t.state !== "finished");
  if (view.trees && open.length === 0) return undefined;
  if (view.driver.blocked) return "Tasks wait: reconnect AlphaLab so Quanty One can go on.";
  if (view.driver.state === "paused") {
    return view.driver.pausedReason === "credits" ? "Tasks paused: out of Credits." : "Tasks paused.";
  }
  const tasks = open.flatMap((t) => t.tasks.filter((k) => !k.review));
  const question = open.flatMap((t) => t.tasks).find((k) => k.label === "waiting_on_you");
  if (question?.question) return `Waiting on you: ${question.question}`;
  const waiting = tasks.find((k) => k.label === "waiting" && k.waitingOn.length);
  if (open.some((t) => t.state === "actionable")) {
    const done = tasks.filter((k) => ["done", "failed", "dropped"].includes(k.label)).length;
    return `Working on your tasks: ${done} of ${tasks.length} done`;
  }
  if (waiting) return `Waiting on ${waiting.waitingOn.map((w) => w.handle.split(":")[1] ?? w.handle).join(", ")}`;
  return "Tasks open: nothing to do until something changes.";
}
